import React, { useEffect, useRef } from 'react';

import NearMeIcon from '@mui/icons-material/NearMe';
import { Button, Grid, IconButton } from '@sunwisesoftware/sunwise-ui';
import { debounce, isEmpty, isNil } from 'lodash';
import PropTypes from 'prop-types';
import { useGeolocated } from 'react-geolocated';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import { ShowErrors } from 'common/components';
import {
    ReactHookFormInput,
    ReactHookFormMap,
    ReactHookFormSelect,
} from 'common/components/form/bootstrap';
import {
    ItemGeocodeResult,
    ListGeocodeResult,
    WrapperGeocodeResult,
} from 'common/components/maps';
import { DEFAULT_LAT, DEFAULT_LNG, DEBOUNCE_TIME_1000 } from 'common/constants';
import { handleOnChangeLatLng } from 'common/utils/helpers';
import yupResolver from 'common/utils/yupResolver';

import * as actions from '../actions';
import { getContactsOptions } from '../helpers';
import * as selectors from '../selectors';
import validate from '../validate';

const ProjectForm = ({
    contacts,
    errors,
    fetchGeocodePoint,
    filterData,
    geocodePointData,
    geocodeResult,
    handleGeocode,
    initialValues,
    isFetchingContacts,
    isFetchingGeocodePoint,
    isNewRecord,
    isSavingProject,
    saveProject,
    setEmptyGeocodeResult,
    setShowGeocodeResults,
    showGeocodeResults,
}) => {
    const { t } = useTranslation();
    const { coords, getPosition, isGeolocationEnabled } = useGeolocated({
        positionOptions: {
            enableHighAccuracy: false,
        },
        userDecisionTimeout: 5000,
        suppressLocationOnMount: true,
    });
    const { control, handleSubmit, reset, setValue, watch } = useForm({
        defaultValues: initialValues,
        resolver: yupResolver(validate),
    });

    const [contact, latitude, longitude, position] = watch([
        'contact',
        'latitude',
        'longitude',
        'position',
    ]);

    const newContact = contact === 'new_contact';

    const onChnageDescription = ({ target }) => {
        if (target.value.length >= 6) handleGeocode(target.value);
        else setEmptyGeocodeResult();
        setValue('address', target.value);
    };

    const debounceOnChnageDescription = useRef(
        debounce((e) => onChnageDescription(e), DEBOUNCE_TIME_1000),
    ).current;

    useEffect(() => {
        if (coords) updateLocation(coords);
    }, [coords]);

    useEffect(() => {
        if (!isEmpty(geocodePointData)) {
            setValue('address', geocodePointData?.address);
            setValue('city', geocodePointData?.city);
            setValue('state', geocodePointData?.state);
            setValue('country', geocodePointData?.country);
        }
    }, [geocodePointData]);

    useEffect(() => reset(initialValues), [initialValues]);

    useEffect(() => {
        if (position) {
            setValue('latitude', position.latitude);
            setValue('longitude', position.longitude);
        }
    }, [position]);

    useEffect(() => {
        if (
            latitude === initialValues.latitude &&
            longitude === initialValues.longitude
        )
            return;
        fetchGeocodePoint(latitude, longitude);
        updateLocation({
            latitude,
            latlong: `${latitude},${longitude}`,
            longitude,
        });
    }, [latitude, longitude]);

    const updateLocation = (position) => {
        if (isEmpty(position)) return;
        const { latitude, longitude } = position;
        const latlng =
            latitude && longitude ? `${latitude}, ${longitude}` : null;
        setValue('latitude', latitude);
        setValue('latlng', latlng);
        setValue('longitude', longitude);
        setValue('position', { latitude, latlng, longitude });
    };

    const handleClickSave = (values) => saveProject(filterData, values);

    return (
        <form>
            <Grid container>
                <Grid size="grow">
                    <ReactHookFormInput
                        control={control}
                        label={t('Project name')}
                        name="name"
                    />

                    <ReactHookFormSelect
                        autoComplete="off"
                        control={control}
                        label={t('Assigned to')}
                        name="contact"
                        options={getContactsOptions(contacts, isNewRecord)}
                        id="contact"
                    />

                    {newContact && (
                        <ReactHookFormInput
                            control={control}
                            label={t('Contact name')}
                            name="contact_name"
                        />
                    )}

                    <Grid container>
                        <Grid size="grow">
                            {geocodeResult &&
                                geocodeResult.length > 0 &&
                                showGeocodeResults && (
                                    <WrapperGeocodeResult>
                                        <ListGeocodeResult
                                            style={{
                                                transform:
                                                    'translate3d(0px, 38px, 0px)',
                                            }}
                                        >
                                            {geocodeResult.map((result, i) => (
                                                <ItemGeocodeResult
                                                    key={i}
                                                    onClick={() => {
                                                        setValue(
                                                            'address',
                                                            result.formatted_address,
                                                        );

                                                        const { lat, lng } =
                                                            result.geometry
                                                                .location;
                                                        updateLocation({
                                                            latitude: lat,
                                                            longitude: lng,
                                                        });
                                                    }}
                                                >
                                                    {result.formatted_address}
                                                </ItemGeocodeResult>
                                            ))}
                                        </ListGeocodeResult>
                                    </WrapperGeocodeResult>
                                )}
                            <ReactHookFormInput
                                control={control}
                                disabled={isFetchingGeocodePoint}
                                name="address"
                                onChange={debounceOnChnageDescription}
                                onBlur={() => {
                                    setTimeout(
                                        () => setShowGeocodeResults(false),
                                        150,
                                    );
                                }}
                                onFocus={() => setShowGeocodeResults(true)}
                                placeholder={t('Location')}
                            />
                        </Grid>
                    </Grid>

                    <Grid container>
                        <Grid size="grow">
                            <ReactHookFormInput
                                control={control}
                                fullWidth
                                id="business_general_lat_lng"
                                onChange={(e) => {
                                    handleOnChangeLatLng({
                                        setValue,
                                        value: e.target.value,
                                    });
                                }}
                                label={`${t('Latitude')}, ${t('Longitude')} (Ej: 19.43290, -99.1332)`}
                                name="latlng"
                            />
                        </Grid>
                        <Grid>
                            <IconButton
                                disabled={
                                    !isGeolocationEnabled || isSavingProject
                                }
                                onClick={() => {
                                    if (!isNil(coords))
                                        updateLocation({
                                            latitude: coords.latitude,
                                            longitude: coords.longitude,
                                        });
                                    else getPosition();
                                }}
                            >
                                <NearMeIcon />
                            </IconButton>
                        </Grid>
                    </Grid>

                    <ReactHookFormMap
                        center={{
                            lat: DEFAULT_LAT,
                            lng: DEFAULT_LNG,
                        }}
                        control={control}
                        id="business_general_address"
                        name="position"
                    />

                    <ShowErrors errors={errors || []} />

                    <Button
                        disabled={
                            isFetchingContacts ||
                            isFetchingGeocodePoint ||
                            isSavingProject
                        }
                        fullWidth
                        onClick={handleSubmit(handleClickSave)}
                        sx={{ textTransform: 'initial' }}
                        variant="outlined"
                    >
                        {isSavingProject
                            ? t('Saving').concat('...')
                            : t('Continue')}
                    </Button>
                </Grid>
            </Grid>
        </form>
    );
};

const mapStateToProps = createStructuredSelector({
    errors: selectors.getSaveProjectErrors,
    geocodePointData: selectors.getGeocodePointData,
    geocodeResult: selectors.getFetchGeocodeData,
    initialValues: selectors.getInitialValues,
    isFetchingGeocodePoint: selectors.getIsFetchingGeocodePoint,
    isSavingProject: selectors.getProjectIsSaving,
    showGeocodeResults: selectors.showGeocodeResults,
});

const mapDispatchToProps = (dispatch) => ({
    fetchGeocodePoint: (lat, lng) =>
        dispatch(actions.fetchGeocodePoint(lat, lng)),
    handleGeocode: (str) => dispatch(actions.fetchGeocodeResult(str)),
    saveProject: (filterData, values) =>
        dispatch(actions.saveProject(filterData, values)),
    setEmptyGeocodeResult: () => dispatch(actions.setEmptyGeocodeResult()),
    setShowGeocodeResults: (value) =>
        dispatch(actions.setShowGeocodeResults(value)),
});

ProjectForm.propTypes = {
    contacts: PropTypes.array,
    errors: PropTypes.array,
    fetchGeocodePoint: PropTypes.func,
    filterData: PropTypes.object,
    geocodePointData: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    geocodeResult: PropTypes.array,
    handleGeocode: PropTypes.func,
    initialValues: PropTypes.object,
    isFetchingContacts: PropTypes.bool,
    isFetchingGeocodePoint: PropTypes.bool,
    isNewRecord: PropTypes.bool,
    isSavingProject: PropTypes.bool,
    saveProject: PropTypes.func,
    setEmptyGeocodeResult: PropTypes.func,
    setShowGeocodeResults: PropTypes.func,
    showGeocodeResults: PropTypes.bool,
};

export default connect(mapStateToProps, mapDispatchToProps)(ProjectForm);
